import React, { useState, useEffect } from "react";
import {
  Settings,
  Save,
  TestTube,
  CheckCircle,
  XCircle,
  Loader2,
  Info,
} from "lucide-react";
import api from "../api/client";

const BUSINESS_TYPES = [
  { value: "LocalBusiness", label: "Local Business" },
  { value: "Dentist", label: "Dentist" },
  { value: "HomeImprovement", label: "Home Improvement" },
  { value: "Restaurant", label: "Restaurant" },
  { value: "LegalService", label: "Legal Service" },
  { value: "MedicalBusiness", label: "Medical Business" },
  { value: "RealEstateAgent", label: "Real Estate" },
  { value: "FinancialService", label: "Financial Service" },
  { value: "AutomotiveBusiness", label: "Automotive" },
  { value: "ProfessionalService", label: "Professional Service" },
  { value: "Other", label: "Other" },
];

function Field({ label, help, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1">
        {label}
      </label>
      {children}
      {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
    </div>
  );
}

function Toggle({ label, help, checked, onChange }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-dark-50 last:border-0">
      <div>
        <div className="text-sm font-medium text-gray-300">{label}</div>
        {help && <div className="text-xs text-gray-500 mt-0.5">{help}</div>}
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? "bg-copper" : "bg-dark-50"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
}

export default function SiteSettings({ selectedSite }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!selectedSite?.id) return;
    setLoading(true);
    api
      .get(`/websites/${selectedSite.id}/site-settings/`)
      .then((res) => setSettings(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedSite?.id]);

  const update = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!selectedSite?.id) return;
    setSaving(true);
    try {
      const res = await api.patch(
        `/websites/${selectedSite.id}/site-settings/`,
        settings
      );
      setSettings(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  const handleTestConnection = async () => {
    if (!selectedSite?.id) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post(
        `/websites/${selectedSite.id}/site-settings/test-connection/`
      );
      setTestResult({ ok: true, message: res.data.message });
    } catch (e) {
      setTestResult({
        ok: false,
        message: e.response?.data?.message || "Connection failed",
      });
    }
    setTesting(false);
  };

  if (!selectedSite) {
    return (
      <div className="p-8 text-center text-gray-500">
        Select a website to configure settings.
      </div>
    );
  }

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-copper" size={24} />
      </div>
    );
  }

  const inputCls =
    "w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:border-copper focus:outline-none";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="text-copper" size={24} />
          <div>
            <h1 className="text-xl font-bold text-white">Site Settings</h1>
            <p className="text-sm text-gray-500">{settings.domain}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="flex items-center gap-1 text-green-400 text-sm">
              <CheckCircle size={14} /> Saved
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <Save size={16} />
            )}
            Save Settings
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Business Information */}
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white border-b border-dark-50 pb-3">
            Business Information
          </h2>

          <Field label="Business Name">
            <input
              className={inputCls}
              value={settings.business_name || ""}
              onChange={(e) => update("business_name", e.target.value)}
              placeholder="Your Business Name"
            />
          </Field>

          <Field label="Business Type">
            <select
              className={inputCls}
              value={settings.business_type || "LocalBusiness"}
              onChange={(e) => update("business_type", e.target.value)}
            >
              {BUSINESS_TYPES.map((bt) => (
                <option key={bt.value} value={bt.value}>
                  {bt.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Phone Number">
            <input
              className={inputCls}
              value={settings.phone_number || ""}
              onChange={(e) => update("phone_number", e.target.value)}
              placeholder="(347) 961-7357"
            />
          </Field>

          <Field label="Contact Email">
            <input
              className={inputCls}
              type="email"
              value={settings.contact_email || ""}
              onChange={(e) => update("contact_email", e.target.value)}
              placeholder="info@example.com"
            />
          </Field>

          <Field label="Address">
            <input
              className={inputCls}
              value={settings.address || ""}
              onChange={(e) => update("address", e.target.value)}
              placeholder="123 Main St, City, State ZIP"
            />
          </Field>

          <Field label="Primary Service">
            <input
              className={inputCls}
              value={settings.primary_service || ""}
              onChange={(e) => update("primary_service", e.target.value)}
              placeholder="e.g., Dental Office Construction"
            />
          </Field>

          <Field
            label="Service Areas"
            help="Comma-separated list of service areas"
          >
            <input
              className={inputCls}
              value={settings.service_areas || ""}
              onChange={(e) => update("service_areas", e.target.value)}
              placeholder="NYC, Westchester, Long Island, NJ"
            />
          </Field>

          <Field label="Certifications">
            <input
              className={inputCls}
              value={settings.certifications || ""}
              onChange={(e) => update("certifications", e.target.value)}
              placeholder="A-dec Certified, Midmark Certified"
            />
          </Field>

          <Field label="Unique Selling Points">
            <input
              className={inputCls}
              value={settings.unique_selling_points || ""}
              onChange={(e) => update("unique_selling_points", e.target.value)}
              placeholder="Licensed & insured, 10+ years experience"
            />
          </Field>
        </div>

        {/* Branding & Templates */}
        <div className="space-y-6">
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white border-b border-dark-50 pb-3">
              Branding
            </h2>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Primary Color">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.brand_color_primary || "#1e3a5f"}
                    onChange={(e) =>
                      update("brand_color_primary", e.target.value)
                    }
                    className="h-10 w-10 rounded cursor-pointer"
                  />
                  <input
                    className={inputCls}
                    value={settings.brand_color_primary || "#1e3a5f"}
                    onChange={(e) =>
                      update("brand_color_primary", e.target.value)
                    }
                  />
                </div>
              </Field>

              <Field label="Accent Color">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.brand_color_accent || "#f59e0b"}
                    onChange={(e) =>
                      update("brand_color_accent", e.target.value)
                    }
                    className="h-10 w-10 rounded cursor-pointer"
                  />
                  <input
                    className={inputCls}
                    value={settings.brand_color_accent || "#f59e0b"}
                    onChange={(e) =>
                      update("brand_color_accent", e.target.value)
                    }
                  />
                </div>
              </Field>
            </div>

            <Field label="Site Group" help="Group related sites together">
              <input
                className={inputCls}
                value={settings.site_group || ""}
                onChange={(e) => update("site_group", e.target.value)}
                placeholder="e.g., GCMM"
              />
            </Field>
          </div>

          {/* Meta Templates */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white border-b border-dark-50 pb-3">
              Meta Description Templates
            </h2>
            <div className="flex items-start gap-2 p-3 bg-dark-100 rounded-lg">
              <Info size={14} className="text-copper mt-0.5 shrink-0" />
              <p className="text-xs text-gray-400">
                Use placeholders:{" "}
                <code className="text-copper">
                  {"{business_name}"} {"{phone}"} {"{primary_service}"}{" "}
                  {"{location}"} {"{title}"}
                </code>
              </p>
            </div>

            <Field
              label="Service Pages Template"
              help="Template for service page meta descriptions"
            >
              <textarea
                className={`${inputCls} h-20`}
                value={settings.meta_template_service || ""}
                onChange={(e) =>
                  update("meta_template_service", e.target.value)
                }
                placeholder="{title}. {business_name} — expert {primary_service}. Call {phone}."
              />
            </Field>

            <Field
              label="Location Pages Template"
              help="Template for location page meta descriptions"
            >
              <textarea
                className={`${inputCls} h-20`}
                value={settings.meta_template_location || ""}
                onChange={(e) =>
                  update("meta_template_location", e.target.value)
                }
                placeholder="{primary_service} in {location}. {business_name} — licensed & insured. Call {phone}."
              />
            </Field>

            <Field
              label="Blog Posts Template"
              help="Template for blog post meta descriptions"
            >
              <textarea
                className={`${inputCls} h-20`}
                value={settings.meta_template_blog || ""}
                onChange={(e) => update("meta_template_blog", e.target.value)}
                placeholder="{title}. Expert insights from {business_name}. Call {phone}."
              />
            </Field>
          </div>

          {/* WordPress Connection */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white border-b border-dark-50 pb-3">
              WordPress Connection
            </h2>

            <Field label="WP API URL">
              <input
                className={`${inputCls} text-gray-500`}
                value={settings.wp_api_url || "Not connected"}
                readOnly
              />
            </Field>

            <div className="flex items-center gap-3">
              <Field label="Pages Synced">
                <span className="text-sm text-gray-400 font-mono">
                  {settings.wp_page_count || 0}
                </span>
              </Field>
              <Field label="Last Sync">
                <span className="text-sm text-gray-400 font-mono">
                  {settings.wp_last_sync
                    ? new Date(settings.wp_last_sync).toLocaleDateString()
                    : "Never"}
                </span>
              </Field>
            </div>

            <button
              onClick={handleTestConnection}
              disabled={testing}
              className="flex items-center gap-2 px-3 py-2 border border-dark-50 hover:border-copper text-gray-400 hover:text-copper rounded-lg text-sm transition-colors"
            >
              {testing ? (
                <Loader2 className="animate-spin" size={14} />
              ) : (
                <TestTube size={14} />
              )}
              Test Connection
            </button>

            {testResult && (
              <div
                className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                  testResult.ok
                    ? "bg-green-900/20 text-green-400 border border-green-800/30"
                    : "bg-red-900/20 text-red-400 border border-red-800/30"
                }`}
              >
                {testResult.ok ? (
                  <CheckCircle size={14} />
                ) : (
                  <XCircle size={14} />
                )}
                {testResult.message}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Automation Toggles */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white border-b border-dark-50 pb-3 mb-3">
          SEO Automation
        </h2>
        <Toggle
          label="Auto Meta Descriptions"
          help="Automatically generate and push missing meta descriptions (daily at 2:30 AM)"
          checked={settings.auto_meta_enabled}
          onChange={(v) => update("auto_meta_enabled", v)}
        />
        <Toggle
          label="Auto Title Optimization"
          help="Fix duplicate, too-short, and too-long title tags automatically"
          checked={settings.auto_titles_enabled}
          onChange={(v) => update("auto_titles_enabled", v)}
        />
        <Toggle
          label="Orphan Page Detection"
          help="Detect pages with 0 internal links and suggest link sources (weekly)"
          checked={settings.auto_orphans_enabled}
          onChange={(v) => update("auto_orphans_enabled", v)}
        />
        <Toggle
          label="Auto Alt Text"
          help="Generate alt text for images missing it"
          checked={settings.auto_alt_text_enabled}
          onChange={(v) => update("auto_alt_text_enabled", v)}
        />
        <Toggle
          label="Weekly SEO Report"
          help="Email a weekly SEO performance report with score changes and auto-fix summary"
          checked={settings.weekly_report_enabled}
          onChange={(v) => update("weekly_report_enabled", v)}
        />
      </div>
    </div>
  );
}
